import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import IconButton from '@material-ui/core/IconButton';
import { observer } from 'mobx-react';

import { Label } from 'core';
import AddButton from 'core/components/add-button';
import { Button } from 'core/components/button';
import RemoveButton from 'core/components/remove-button';
import { Tooltip } from 'core/components/tooltip';
import { numberFormatter } from 'core/helpers/formatters';
import editIcon from 'core/components/editable-card/edit.svg';
import { CardLine } from '../../card-line';
import { track } from 'core/helpers/analytics';
import { useStore } from 'core/store/';
import greenCheckIcon from 'core/assets/svg/green-check.svg';
import arrowIcon from 'core/assets/svg/up-right-arrow.svg';
import useStyles from './discount.styles';

const money = (value) => `$${numberFormatter(value)}`;
const percentage = (value) => `${value}%`;

const Discount = ({
  label,
  description,
  amount,
  possibleSavings,
  isActive,
  canEdit,
  onRemove,
  onAdd,
  connectedHomeInfo = {},
  disabled,
  includeBranchPlusMultiPolicyDiscount,
  appcuesId,
  id: htmlContainerId
}) => {
  const classes = useStyles();
  const {
    offer: {
      offer: {
        id,
        quote: {
          correctedAddress: { state }
        }
      }
    }
  } = useStore();

  const discountLabel = () => {
    if (label === 'Windstorm Mitigation' || (isActive && !amount)) {
      return 'Enrolled';
    }
    if (label === 'Branch Plus Bundle') {
      if (isActive && includeBranchPlusMultiPolicyDiscount && amount === 0) {
        return 'Update to see...';
      }
      if (!isActive) {
        return 'Bundle And Save';
      }
    }
    if (
      label === 'Connected Home' &&
      ((connectedHomeInfo && connectedHomeInfo.includeConnectedHome && !isActive) ||
        (!connectedHomeInfo.includeConnectedHome && isActive))
    ) {
      return 'Update to see...';
    }

    if ((label === 'Bundle' || label === 'Multiple Cars') && !isActive) {
      return `Save up to ${money(amount)}`;
    }

    return isActive && amount ? `Saving ${money(amount)}` : `Save up to ${percentage(possibleSavings)}`;
  };

  const handleMouseOver = () => {
    track(`${label} Tooltip Triggered`, { discount: label, offerId: id, state });
  };

  return (
    <CardLine className={classNames(classes.discount, { [classes.inactive]: !isActive })}>
      <div className={classes.section} id={htmlContainerId}>
        <Label type="discount">
          {label}
          {appcuesId && (
            <Button
              color="secondary"
              variant="text"
              className={classes.info}
              onClick={() => window?.Appcues?.show(appcuesId)}
            >
              <img src={arrowIcon} alt="arrow" className={classes.arrow} />
            </Button>
          )}
        </Label>
        {description && (
          <Tooltip className={classes.info} text={description} onMouseEnter={handleMouseOver} placement="top" />
        )}
      </div>
      <div className={classes.section}>
        <Label className={classes.savings} type="discount">
          {discountLabel()}
        </Label>
        {isActive && !canEdit && !onRemove && <img src={greenCheckIcon} alt="check" />}
        {isActive && canEdit && (
          <IconButton onClick={onAdd} className={classes.iconContainer} disabled={disabled}>
            <img src={editIcon} alt="edit" size="small" className={classes.editIcon} />
          </IconButton>
        )}
        {isActive && onRemove && <RemoveButton onClick={onRemove} disabled={disabled} />}
        {!isActive && onAdd && <AddButton type="full" className={classes.button} onClick={onAdd} disabled={disabled} />}
      </div>
    </CardLine>
  );
};

Discount.propTypes = {
  label: PropTypes.string.isRequired,
  description: PropTypes.string,
  amount: PropTypes.number.isRequired,
  possibleSavings: PropTypes.number,
  isActive: PropTypes.bool,
  canEdit: PropTypes.bool,
  onAdd: PropTypes.func,
  onRemove: PropTypes.func,
  appcuesId: PropTypes.string
};

Discount.defaultProps = {
  isActive: false,
  canEdit: false,
  description: null,
  onAdd: null,
  onRemove: null,
  possibleSavings: 0,
  appcuesId: null
};

export default observer(Discount);
